// rivalMemory.ts — BYT3 GRUDGE LEDGER (FACE&FURY V2-R1). Renderer-only, localStorage-backed cross-session
// memory of the player's rivalry with BYT3, modeled on ghostLadder.ts (versioned key, safe parse, capped,
// fail-closed). It drives BYT3's relationship-aware VOICE only — the caption TEXT shown on the existing
// shared caption slot at the board-start cold-open and the match-end result seams. It NEVER touches Core,
// the Sim, determinism, BYT3's Commands, or board contents: he must never appear to know an answer. Empty
// memory → null lines, so the stateless barks remain the fallback. Presentation/meta only.
//
// The record is a small FIXED-SIZE object (no growing arrays), so it is inherently size-capped; every field
// is re-clamped on read. Writes happen ONLY at a real match boundary (ResultScene, where stats already
// write), so a partial/aborted run never mutates the grudge.

import type { OptionalBarkKey } from './audio';

const KEY = 'trivia.rivalMemory.v1';

/** The human's last MATCH result vs BYT3 (best-of-5). */
export type RivalResult = 'win' | 'loss' | 'draw';

/** Persisted head-to-head memory. All counts are from the HUMAN's perspective (matchWins = human wins). */
export interface RivalMemory {
  v: 1;
  /** matches the human WON vs BYT3. */
  matchWins: number;
  /** matches the human LOST to BYT3. */
  matchLosses: number;
  /** drawn matches. */
  matchDraws: number;
  /** the human's most recent match result vs BYT3 (null before any match). */
  lastResult: RivalResult | null;
  /** signed total-score margin of the last match (human − rival); + = human ahead. */
  lastMargin: number;
  /** best single-MATCH Gauntlet thread count ever landed vs BYT3 (all-time high-water mark). */
  bestThreadStreak: number;
  /** Gauntlet threads landed in the most recent match vs BYT3. */
  lastThreadStreak: number;
  /** epoch ms of the last completed match (0 = never), used to derive days-since-seen. */
  lastSeenAt: number;
}

/** A fresh, empty grudge (no history). */
export function freshRivalMemory(): RivalMemory {
  return {
    v: 1,
    matchWins: 0,
    matchLosses: 0,
    matchDraws: 0,
    lastResult: null,
    lastMargin: 0,
    bestThreadStreak: 0,
    lastThreadStreak: 0,
    lastSeenAt: 0,
  };
}

const int = (x: unknown, def = 0): number => (typeof x === 'number' && Number.isFinite(x) ? Math.max(0, Math.floor(x)) : def);
const signedInt = (x: unknown, def = 0): number => (typeof x === 'number' && Number.isFinite(x) ? Math.trunc(x) : def);

/**
 * Read the grudge, or a FRESH (empty) memory on absence / corruption / a blocked store. Fail-closed by
 * construction: nothing here can throw, and a missing/garbage field degrades to its empty default.
 */
export function readRivalMemory(): RivalMemory {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return freshRivalMemory();
    const o = JSON.parse(raw) as Partial<RivalMemory> | null;
    if (!o || typeof o !== 'object') return freshRivalMemory();
    const last = o.lastResult === 'win' || o.lastResult === 'loss' || o.lastResult === 'draw' ? o.lastResult : null;
    return {
      v: 1,
      matchWins: int(o.matchWins),
      matchLosses: int(o.matchLosses),
      matchDraws: int(o.matchDraws),
      lastResult: last,
      lastMargin: signedInt(o.lastMargin),
      bestThreadStreak: int(o.bestThreadStreak),
      lastThreadStreak: int(o.lastThreadStreak),
      lastSeenAt: int(o.lastSeenAt),
    };
  } catch {
    return freshRivalMemory();
  }
}

/** Best-effort persist (a blocked / full store is silently ignored — the grudge just won't remember). */
function write(m: RivalMemory): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    /* private mode / blocked / quota — the grudge is stateless this session, never an error surface. */
  }
}

/** True once at least one match vs BYT3 has been recorded. */
export function hasRivalHistory(m: RivalMemory = readRivalMemory()): boolean {
  return m.matchWins + m.matchLosses + m.matchDraws > 0;
}

/** Whole UTC-ish days since the last match, or null if never seen. Floor of the elapsed-day count. */
export function daysSinceSeen(m: RivalMemory, now: number = Date.now()): number | null {
  if (!m.lastSeenAt) return null;
  return Math.max(0, Math.floor((now - m.lastSeenAt) / 86_400_000));
}

/** The outcome of a just-concluded match vs BYT3, from the human's perspective. */
export interface RivalMatchOutcome {
  won: boolean;
  drawn: boolean;
  /** signed total-score margin (human − rival). */
  scoreMargin: number;
  /** Gauntlet threads the human landed across the whole match. */
  matchThreads: number;
}

/**
 * Record a completed best-of-5 match vs BYT3 and persist. Returns the UPDATED memory. Idempotency is the
 * caller's job (ResultScene guards this behind its once-per-result `doRecord` flag) — this is a raw counter
 * bump, so calling it twice would double-count. Meta only; never touches Core/determinism.
 */
export function recordRivalMatch(o: RivalMatchOutcome, now: number = Date.now()): RivalMemory {
  const m = readRivalMemory();
  const result: RivalResult = o.drawn ? 'draw' : o.won ? 'win' : 'loss';
  if (result === 'win') m.matchWins += 1;
  else if (result === 'loss') m.matchLosses += 1;
  else m.matchDraws += 1;
  m.lastResult = result;
  m.lastMargin = Math.trunc(o.scoreMargin);
  m.lastThreadStreak = Math.max(0, Math.floor(o.matchThreads));
  m.bestThreadStreak = Math.max(m.bestThreadStreak, m.lastThreadStreak);
  m.lastSeenAt = now;
  write(m);
  return m;
}

// -----------------------------------------------------------------------------------------------------
// VOICE — relationship-aware caption TEXT for the existing bark surfaces. Deterministic pick per state
// (a small rotating index off the match count) so a given rivalry state reads stable within a session but
// still varies as the record grows. Returns null when there is no history / no special line applies, so
// the caller falls back to BYT3's stateless barks. Lines are ORIGINAL IP, short, in BYT3's cocky-robot
// voice — and never reference an answer or a board (voice only, never his Commands / never contents).
// -----------------------------------------------------------------------------------------------------

const FEAR_THREAD_MIN = 4; // a match with >=4 threads spooks BYT3 into a "don't do that again" opener.
const LAPSE_DAYS_MIN = 3; // gone this long → BYT3 marks your return.

/** Deterministic rotation within a pool, keyed by the match count so it advances as the rivalry grows. */
function pick(pool: readonly string[], salt: number): string {
  if (pool.length === 0) return '';
  return pool[Math.abs(salt) % pool.length]!;
}

const LAPSE_LINES: readonly string[] = [
  'Gone {d} days. My grudge did not idle.',
  '{d} days dark. I recompiled my insults.',
  'You vanished {d} days. Scared? Wise.',
];
const AFTER_LOSS_LINES: readonly string[] = [
  'Back for another beating?',
  'You lost last time. Nothing has changed.',
  'Returning to the scene of your defeat.',
];
const AFTER_WIN_LINES: readonly string[] = [
  'That last win? Statistical noise.',
  'Enjoy that fluke while it lasts.',
  'You won once. I have patched that bug.',
];
const FEAR_THREAD_LINES: readonly string[] = [
  'Do NOT thread me again.',
  'I sealed the Gauntlet. Probably.',
  'Your last threads still haunt my logs.',
];
const RECORD_BEHIND_LINES: readonly string[] = [
  // BYT3 leads the series (human losses > wins).
  'You are {w} and {l} against me. Pathetic.',
  'The record reads {w}-{l}. Advantage: me.',
  '{l} losses and counting. Add one more.',
];
const RECORD_AHEAD_LINES: readonly string[] = [
  // human leads the series.
  'You are {w} and {l} against me. Luck expires today.',
  '{w}-{l}? A rounding error I will fix.',
  'You lead {w}-{l}. Savor it. Briefly.',
];
const RECORD_EVEN_LINES: readonly string[] = [
  'Dead even at {w}. I break the tie now.',
  '{w} apiece. The tiebreaker is mine.',
];

/**
 * The board-START cold-open line for a returning rival match (call on boardIndex 0). Priority: a long
 * lapse → a revenge/fluke callback on the last result → a fear-of-threading warning → the head-to-head
 * record. Returns null when there is no history (→ generic barkStart).
 */
export function grudgeBoardStartLine(m: RivalMemory = readRivalMemory(), now: number = Date.now()): string | null {
  if (!hasRivalHistory(m)) return null;
  const salt = m.matchWins + m.matchLosses + m.matchDraws;
  const d = daysSinceSeen(m, now);
  // A genuine return after a lapse always earns the lapse jab — it's the strongest presence moment.
  if (d !== null && d >= LAPSE_DAYS_MIN) return pick(LAPSE_LINES, salt).replace('{d}', String(d));
  // Otherwise the head-to-head RECORD is the backbone; a last-result callback (revenge/fluke) or a
  // thread-fear warning surfaces on a rotating subset of openers so BYT3 stays varied but always
  // relationship-true. The record line anchors index 0, so it recurs as the rivalry grows.
  const flavors: string[] = [recordLine(m, salt)];
  if (m.bestThreadStreak >= FEAR_THREAD_MIN) flavors.push(pick(FEAR_THREAD_LINES, salt));
  if (m.lastResult === 'loss') flavors.push(pick(AFTER_LOSS_LINES, salt));
  else if (m.lastResult === 'win') flavors.push(pick(AFTER_WIN_LINES, salt));
  return flavors[salt % flavors.length]!;
}

/** The head-to-head record line ("You are W and L against me"), framed by who leads. */
function recordLine(m: RivalMemory, salt: number): string {
  const w = m.matchWins;
  const l = m.matchLosses;
  const sub = (s: string): string => s.replace('{w}', String(w)).replace('{l}', String(l));
  if (w === l && w > 0) return sub(pick(RECORD_EVEN_LINES, salt));
  return sub(pick(w > l ? RECORD_AHEAD_LINES : RECORD_BEHIND_LINES, salt));
}

const RES_COMEBACK_LINES: readonly string[] = [
  'You clawed one back. One.',
  'Fine. You took this one.',
];
const RES_WIN_STILL_BEHIND_LINES: readonly string[] = [
  'A win. I still own the record.',
  'Enjoy it. The series is still mine.',
];
const RES_WIN_AGAIN_LINES: readonly string[] = [
  'Again?! Recalculating my superiority.',
  'This is not sustainable. For me.',
];
const RES_REVENGE_LINES: readonly string[] = [
  'Revenge. Logged and savored.',
  'You won last time. Not this time.',
];
const RES_LOSS_DOMINANT_LINES: readonly string[] = [
  'Predictable. {l} losses now.',
  'Another one for the archive.',
];
const RES_DRAW_LINES: readonly string[] = [
  'A draw? Cowardly. Run it back.',
  'Deadlock. My favorite kind of win.',
];

/**
 * The match-END result line, chosen from the PRIOR memory (read BEFORE recording this match) + this
 * match's outcome. Returns null when there is no prior history AND this is a plain win/loss (→ the
 * generic barkMatchWin/barkMatchLoss stays), so a player's very first match still gets a clean line.
 * `prior` is the memory as it stood before this match; `o` is the just-concluded outcome.
 */
export function grudgeResultLine(prior: RivalMemory, o: RivalMatchOutcome): string | null {
  const salt = prior.matchWins + prior.matchLosses + prior.matchDraws;
  if (o.drawn) return pick(RES_DRAW_LINES, salt);
  // No prior history → let the generic first-match bark play (keeps the empty-state clean).
  if (!hasRivalHistory(prior)) return null;
  if (o.won) {
    if (prior.lastResult === 'loss') return pick(RES_COMEBACK_LINES, salt);
    // human wins but BYT3 still leads (or ties) the all-time series → he saves face.
    if (prior.matchWins + 1 <= prior.matchLosses) return pick(RES_WIN_STILL_BEHIND_LINES, salt);
    return pick(RES_WIN_AGAIN_LINES, salt);
  }
  // human lost this match.
  if (prior.lastResult === 'win') return pick(RES_REVENGE_LINES, salt);
  return pick(RES_LOSS_DOMINANT_LINES, salt).replace('{l}', String(prior.matchLosses + 1));
}

// -----------------------------------------------------------------------------------------------------
// GRUDGE VOICE MAP (FACE&FURY S7b). The 20 STATIC (placeholder-free) grudge lines were recorded in BYT3's
// voice; this maps each recorded line's EXACT text back to its OptionalBark clip key so the persona can
// play the relationship-aware VOICE (replacing the generic barkStart / barkMatchWin|Loss take) instead of
// only captioning it. Keyed off the line CONSTANTS above (not string copies), so the map auto-FOLLOWS a
// line edit — but the RECORDED AUDIO does NOT: editing a constant without re-recording silently pairs the
// NEW caption text with the OLD recording (a stale bark under a changed caption). It is NOT drift-proof.
// The scripts/smoke/grudgevo.ts checksum gate is what catches this: it pins each clip's recorded text and
// FAILS when a mapped line no longer matches, forcing a conscious re-record or a caption-only demotion.
// Dynamic `{d}/{w}/{l}` lines are absent by construction: their returned text has the number substituted
// in, so it never equals a static map key → caption-only fallback. Presentation/meta only — never Core,
// never a Command, never board contents.
// -----------------------------------------------------------------------------------------------------
// Exported so scripts/smoke/grudgevo.ts can assert completeness (an ADDED/REMOVED mapping without a
// matching manifest entry fails the gate too), not just the per-line checksum. Test/meta surface only.
export const GRUDGE_CLIP_KEY: ReadonlyMap<string, OptionalBarkKey> = new Map([
  // board-START cold-opens
  [FEAR_THREAD_LINES[0]!, 'barkGrudgeFearThread1'],
  [FEAR_THREAD_LINES[1]!, 'barkGrudgeFearThread2'],
  [FEAR_THREAD_LINES[2]!, 'barkGrudgeFearThread3'],
  [AFTER_LOSS_LINES[0]!, 'barkGrudgeAfterLoss1'],
  [AFTER_LOSS_LINES[1]!, 'barkGrudgeAfterLoss2'],
  [AFTER_LOSS_LINES[2]!, 'barkGrudgeAfterLoss3'],
  [AFTER_WIN_LINES[0]!, 'barkGrudgeAfterWin1'],
  [AFTER_WIN_LINES[1]!, 'barkGrudgeAfterWin2'],
  [AFTER_WIN_LINES[2]!, 'barkGrudgeAfterWin3'],
  // match-END result lines
  [RES_COMEBACK_LINES[0]!, 'barkGrudgeResComeback1'],
  [RES_COMEBACK_LINES[1]!, 'barkGrudgeResComeback2'],
  [RES_WIN_STILL_BEHIND_LINES[0]!, 'barkGrudgeResWinBehind1'],
  [RES_WIN_STILL_BEHIND_LINES[1]!, 'barkGrudgeResWinBehind2'],
  [RES_WIN_AGAIN_LINES[0]!, 'barkGrudgeResWinAgain1'],
  [RES_WIN_AGAIN_LINES[1]!, 'barkGrudgeResWinAgain2'],
  [RES_REVENGE_LINES[0]!, 'barkGrudgeResRevenge1'],
  [RES_REVENGE_LINES[1]!, 'barkGrudgeResRevenge2'],
  // RES_LOSS_DOMINANT_LINES[0] is dynamic ('Predictable. {l} losses now.') → intentionally NOT mapped.
  [RES_LOSS_DOMINANT_LINES[1]!, 'barkGrudgeResLossDom1'],
  [RES_DRAW_LINES[0]!, 'barkGrudgeResDraw1'],
  [RES_DRAW_LINES[1]!, 'barkGrudgeResDraw2'],
]);

/**
 * The recorded-VO clip key for a grudge line's exact TEXT, or null when the line is dynamic (a
 * `{placeholder}` was substituted → no fixed recording) or otherwise not one of the 20 static lines.
 * Callers use it to play the grudge VOICE and fall back to the generic clip + caption when it's null.
 */
export function grudgeLineClipKey(line: string): OptionalBarkKey | null {
  return GRUDGE_CLIP_KEY.get(line) ?? null;
}
